import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEvent, XkStation, XkStock } from '@/lib/api/types';
import { allocationsApi } from '@/features/stock/api';
import { gameLinkUrl, type GameLink } from '@/features/events/defaultJourney';

/**
 * How far along the three-step path (task 8.2) this brand is.
 *
 * Lives apart from the card because two surfaces need the same answer: the
 * quick start decides whether to show itself, and the home decides between the
 * quick start and the returning-user summary (task 8.5). Two copies of "are we
 * done?" would eventually disagree and show both, or neither.
 *
 * The queries use the page's own keys, so nothing here costs an extra request.
 */
export interface QuickStartState {
  hasXotski: boolean;
  hasEvent: boolean;
  /** The newest event — what the manager means by "my game". */
  targetEvent: XkEvent | null;
  /** Live link derived from an existing station, when one is already wired. */
  link: GameLink | null;
  loading: boolean;
  /** All three steps walked. */
  done: boolean;
}

export function useQuickStartState(brandId: number, enabled: boolean): QuickStartState {
  const stockQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId),
    queryFn: () => api.list<XkStock>('/xk-stock', { filter: { companyId: brandId } }),
    enabled,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId } }),
    enabled,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId } }),
    enabled,
  });

  const events = eventsQ.data?.items ?? [];
  const stations = stationsQ.data?.items ?? [];
  const targetEvent = events.length
    ? events.reduce((a, b) => (b.id > a.id ? b : a))
    : null;
  const readyStation = targetEvent
    ? stations.find((s) => s.eventId === targetEvent.id && s.surveysId)
    : undefined;

  // Whether the newest event's station actually has prizes on it. A station
  // with a journey but no allocations serves the guest a game screen with
  // nothing on it, so "get your game link" is not done until this is true.
  const allocationsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byEvent(targetEvent?.id ?? 0),
    queryFn: () => allocationsApi.list({ eventId: targetEvent!.id }),
    enabled: enabled && !!targetEvent,
  });
  const hasPrizes = (allocationsQ.data?.items ?? []).some(
    (a) => a.status === 'Enable' && a.quantity > 0,
  );

  const link: GameLink | null = readyStation?.surveysId && hasPrizes
    ? {
        surveyId: readyStation.surveysId,
        stationId: readyStation.id,
        url: gameLinkUrl(readyStation.surveysId, readyStation.id),
      }
    : null;

  const hasXotski = (stockQ.data?.items.length ?? 0) > 0;
  const hasEvent = events.length > 0;
  // `isLoading` is FALSE for a disabled query — TanStack reports it as pending
  // but not loading. Reading it directly meant this hook announced "settled,
  // nothing here" during the frames before the access check enabled the
  // queries, and callers that latch a decision on the first settled read latched
  // the wrong one: the home rendered neither the checklist nor the summary.
  const loading =
    !enabled ||
    stockQ.isPending ||
    eventsQ.isPending ||
    stationsQ.isPending ||
    (!!targetEvent && allocationsQ.isPending);

  return {
    hasXotski,
    hasEvent,
    targetEvent,
    link,
    loading,
    done: hasXotski && hasEvent && !!link,
  };
}
